import React, { useState, useEffect } from 'react';
import {
  FileText, Plus, Edit, Trash2, Eye, Save, Download, Calendar,
  Clock, Users, BarChart3, PieChart, LineChart, TrendingUp,
  Settings, Filter, Search, Share2, Copy, Archive, RefreshCw,
  ChevronDown, ChevronRight, MoreHorizontal, X, CheckCircle2,
  AlertCircle, Target, Zap, Activity, Globe, Mail, Bell, Play
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const CustomReportBuilder = () => {
  const [reports, setReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [reportData, setReportData] = useState({
    name: '',
    description: '',
    category: 'performance',
    dataSource: 'tickets',
    filters: [],
    metrics: [],
    visualization: 'table',
    schedule: null,
    recipients: []
  });

  useEffect(() => {
    loadReportData();
  }, []);

  const loadReportData = async () => {
    try {
      setLoading(true);
      const [reportsResponse, templatesResponse] = await Promise.all([
        AdminService.getCustomReports(),
        AdminService.getReportTemplates()
      ]);
      
      if (reportsResponse.success) {
        setReports(reportsResponse.data);
      }
      if (templatesResponse.success) {
        setTemplates(templatesResponse.data);
      }
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async () => {
    try {
      const response = await AdminService.createCustomReport(reportData);
      if (response.success) {
        setReports([...reports, response.data]);
        setShowCreateForm(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error creating report:', error);
    }
  };

  const handleUpdateReport = async () => {
    try {
      const response = await AdminService.updateCustomReport(editingReport.id, reportData);
      if (response.success) {
        setReports(reports.map(r => r.id === editingReport.id ? response.data : r));
        setEditingReport(null);
        resetForm();
      }
    } catch (error) {
      console.error('Error updating report:', error);
    }
  };

  const handleDeleteReport = async (reportId) => {
    try {
      const response = await AdminService.deleteCustomReport(reportId);
      if (response.success) {
        setReports(reports.filter(r => r.id !== reportId));
      }
    } catch (error) {
      console.error('Error deleting report:', error);
    }
  };

  const handleRunReport = async (reportId) => {
    try {
      const response = await AdminService.runCustomReport(reportId);
      if (response.success) {
        alert('Report generated successfully!');
      }
    } catch (error) {
      console.error('Error running report:', error);
    }
  };

  const handleScheduleReport = async (reportId, scheduleData) => {
    try {
      const response = await AdminService.scheduleCustomReport(reportId, scheduleData);
      if (response.success) {
        alert('Report scheduled successfully!');
      }
    } catch (error) {
      console.error('Error scheduling report:', error);
    }
  };

  const resetForm = () => {
    setReportData({
      name: '',
      description: '',
      category: 'performance',
      dataSource: 'tickets',
      filters: [],
      metrics: [],
      visualization: 'table',
      schedule: null,
      recipients: []
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      draft: 'bg-yellow-100 text-yellow-800',
      scheduled: 'bg-blue-100 text-blue-800',
      archived: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      performance: BarChart3,
      analytics: TrendingUp,
      compliance: Target,
      financial: Globe,
      operational: Activity
    };
    return icons[category] || FileText;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Custom Report Builder</h1>
          <p className="text-gray-600">Create and manage custom reports for your organization</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Report
          </button>
          <button
            onClick={loadReportData}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Report Templates */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Report Templates</h3>
          <p className="text-gray-600">Start with pre-built templates</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => {
              const IconComponent = getCategoryIcon(template.category);
              return (
                <div
                  key={template.id}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 cursor-pointer"
                  onClick={() => {
                    setSelectedTemplate(template);
                    setReportData({
                      ...reportData,
                      name: template.name,
                      description: template.description,
                      category: template.category,
                      dataSource: template.dataSource,
                      metrics: template.metrics,
                      visualization: template.visualization
                    });
                    setShowCreateForm(true);
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <IconComponent className="w-5 h-5 text-blue-600 mr-2" />
                      <h4 className="font-medium text-gray-900">{template.name}</h4>
                    </div>
                    <span className="text-xs text-gray-500">{template.category}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{template.metrics.length} metrics</span>
                    <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                      Use Template
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Custom Reports</h3>
              <p className="text-gray-600">{reports.length} reports created</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search reports..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm">
                <option value="">All Categories</option>
                <option value="performance">Performance</option>
                <option value="analytics">Analytics</option>
                <option value="compliance">Compliance</option>
                <option value="financial">Financial</option>
                <option value="operational">Operational</option>
              </select>
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {reports.map((report) => {
            const IconComponent = getCategoryIcon(report.category);
            return (
              <div key={report.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <IconComponent className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{report.name}</h4>
                      <p className="text-sm text-gray-600">{report.description}</p>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                          {report.status}
                        </span>
                        <span className="text-xs text-gray-500">{report.category}</span>
                        <span className="text-xs text-gray-500">{report.lastRun ? `Last run: ${new Date(report.lastRun).toLocaleDateString()}` : 'Never run'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleRunReport(report.id)}
                      className="flex items-center px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      <Play className="w-4 h-4 mr-1" />
                      Run
                    </button>
                    <button
                      onClick={() => {
                        setEditingReport(report);
                        setReportData(report);
                        setShowCreateForm(true);
                      }}
                      className="flex items-center px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteReport(report.id)}
                      className="flex items-center px-3 py-1 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create/Edit Report Form */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingReport ? 'Edit Report' : 'Create New Report'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingReport(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Report Name</label>
                  <input
                    type="text"
                    value={reportData.name}
                    onChange={(e) => setReportData({ ...reportData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Enter report name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={reportData.category}
                    onChange={(e) => setReportData({ ...reportData, category: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="performance">Performance</option>
                    <option value="analytics">Analytics</option>
                    <option value="compliance">Compliance</option>
                    <option value="financial">Financial</option>
                    <option value="operational">Operational</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={reportData.description}
                  onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  rows={3}
                  placeholder="Enter report description"
                />
              </div>

              {/* Data Source and Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Source</label>
                  <select
                    value={reportData.dataSource}
                    onChange={(e) => setReportData({ ...reportData, dataSource: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="tickets">Tickets</option>
                    <option value="users">Users</option>
                    <option value="agents">Agents</option>
                    <option value="departments">Departments</option>
                    <option value="sla">SLA</option>
                    <option value="performance">Performance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Visualization</label>
                  <select
                    value={reportData.visualization}
                    onChange={(e) => setReportData({ ...reportData, visualization: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="table">Table</option>
                    <option value="bar">Bar Chart</option>
                    <option value="line">Line Chart</option>
                    <option value="pie">Pie Chart</option>
                    <option value="dashboard">Dashboard</option>
                  </select>
                </div>
              </div>

              {/* Metrics Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Metrics</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {['Total Tickets', 'Resolution Time', 'Customer Satisfaction', 'Agent Performance', 'SLA Compliance', 'Escalation Rate', 'First Call Resolution', 'Backlog Size'].map((metric) => (
                    <label key={metric} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={reportData.metrics.includes(metric)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setReportData({ ...reportData, metrics: [...reportData.metrics, metric] });
                          } else {
                            setReportData({ ...reportData, metrics: reportData.metrics.filter(m => m !== metric) });
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{metric}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Scheduling */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Schedule (Optional)</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <select
                    value={reportData.schedule?.frequency || ''}
                    onChange={(e) => setReportData({ 
                      ...reportData, 
                      schedule: { ...reportData.schedule, frequency: e.target.value }
                    })}
                    className="border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">No Schedule</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                  <input
                    type="time"
                    value={reportData.schedule?.time || ''}
                    onChange={(e) => setReportData({ 
                      ...reportData, 
                      schedule: { ...reportData.schedule, time: e.target.value }
                    })}
                    className="border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Time"
                  />
                  <input
                    type="email"
                    value={reportData.recipients.join(', ')}
                    onChange={(e) => setReportData({ 
                      ...reportData, 
                      recipients: e.target.value.split(',').map(email => email.trim()).filter(email => email)
                    })}
                    className="border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Recipients (comma separated)"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-6 border-t">
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingReport(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={editingReport ? handleUpdateReport : handleCreateReport}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {editingReport ? 'Update Report' : 'Create Report'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomReportBuilder;
